import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import type { PvpIntent } from './connection';
import { createPvpController, type PvpController, type PvpControllerDeps } from './controller';
import type { PvpUiState } from './machine';

/**
 * React's view of the duel controller, and nothing more.
 *
 * ## What this deliberately does not contain
 *
 * No reducer, no `fetch`, no `WebSocket`, no operation id, no generation and no
 * asynchronous work of its own. Every one of those already exists in
 * `createPvpController`, where it can be tested against a failing bootstrap and
 * a hidden tab without a renderer. A hook that re-implemented any of them would
 * be a second source of truth about a resource that must be released once.
 *
 * ## Why the dependencies are held in a ref
 *
 * The controller must survive re-renders - it owns a socket, a pause lease and
 * a hidden hunt - so it can be built only once per mount. But the callbacks it
 * needs are ordinary React functions whose identity changes on every render,
 * and a `useMemo` over them would rebuild the controller as a side effect of an
 * unrelated state change. That is not a hypothetical cost: rebuilding it
 * mid-duel would drop the socket, orphan the lease and leave the hunt hidden.
 *
 * So the controller is given a STABLE facade that reads through a ref. The
 * newest callbacks are always the ones called, and the controller's identity
 * never depends on them.
 */
export interface PvpControllerHandle {
  readonly state: PvpUiState;
  readonly open: () => void;
  readonly createChallenge: () => void;
  /** Asks the server whether this account is mid-duel, and rejoins if it is. */
  readonly resume: () => void;
  readonly joinChallenge: (code: string) => void;
  /** Clears a refusal that no longer describes the code being typed. */
  readonly codeEdited: () => void;
  readonly markReady: () => void;
  /** One coalesced intent, straight to the room. */
  readonly sendIntent: (intent: PvpIntent) => void;
  readonly leave: () => void;
  readonly retryHuntRecovery: () => void;
}

export function usePvpController(deps: PvpControllerDeps): PvpControllerHandle {
  /*
   * The newest dependencies, without making them part of the controller's
   * identity. Written during render on purpose: an effect would leave the first
   * frame after a change calling yesterday's callback.
   */
  const latest = useRef(deps);
  latest.current = deps;

  const controller = useMemo<PvpController>(() => {
    const stable: PvpControllerDeps = {
      createChallenge: (signal) => latest.current.createChallenge(signal),
      findActiveMatch: (signal) => latest.current.findActiveMatch(signal),
      leftMatches: {
        remember: (matchId) => {
          latest.current.leftMatches.remember(matchId);
        },
        was: (matchId) => latest.current.leftMatches.was(matchId),
      },
      acceptChallenge: (code, signal) => latest.current.acceptChallenge(code, signal),
      openConnection: () => latest.current.openConnection(),
      acquirePause: () => latest.current.acquirePause(),
      intentSent: (seq, intent) => {
        latest.current.intentSent(seq, intent);
      },
      setHuntVisible: (visible) => {
        latest.current.setHuntVisible(visible);
      },
      clearHuntIntent: () => {
        latest.current.clearHuntIntent();
      },
      openArena: (seat) => {
        latest.current.openArena(seat);
      },
      publishProcs: (cuts) => {
        latest.current.publishProcs(cuts);
      },
      publishBlasts: (blasts) => {
        latest.current.publishBlasts(blasts);
      },
      publishBleedBursts: (bursts) => {
        latest.current.publishBleedBursts(bursts);
      },
      publishReflects: (reflects) => {
        latest.current.publishReflects(reflects);
      },
      publishAuthoritative: (state) => {
        latest.current.publishAuthoritative(state);
      },
      closeArena: () => {
        latest.current.closeArena();
      },
      bootstrapHunt: (signal) => latest.current.bootstrapHunt(signal),
    };
    return createPvpController(stable);
    // Built once per mount. Every dependency reaches it through the ref above.
  }, []);

  useEffect(() => {
    /*
     * The cleanup is the whole reason `dispose` is idempotent.
     *
     * StrictMode mounts, cleans up and mounts again, so this runs against a
     * controller a second effect may already have replaced - and disposing must
     * never release a lease or restore a hunt somebody else is holding.
     */
    return () => {
      controller.dispose();
    };
  }, [controller]);

  const state = useSyncExternalStore(
    // `subscribe` must be stable, or React resubscribes on every render.
    useCallback(
      (onStoreChange: () => void) => controller.subscribe(() => onStoreChange()),
      [controller],
    ),
    /*
     * The controller hands back the SAME object until something changes, which
     * is what `useSyncExternalStore` needs to avoid an infinite render loop. It
     * is not a convenience: a getter that built a new object each call would
     * re-render forever.
     */
    useCallback(() => controller.getState(), [controller]),
    useCallback(() => controller.getState(), [controller]),
  );

  return useMemo(
    () => ({
      state,
      open: () => {
        controller.open();
      },
      createChallenge: () => {
        controller.createMatch();
      },
      resume: () => {
        controller.resume();
      },
      joinChallenge: (code: string) => {
        controller.joinMatch(code);
      },
      codeEdited: () => {
        controller.codeEdited();
      },
      markReady: () => {
        controller.markReady();
      },
      sendIntent: (intent: PvpIntent) => {
        controller.sendIntent(intent);
      },
      leave: () => {
        controller.leave();
      },
      retryHuntRecovery: () => {
        controller.retryHuntRecovery();
      },
    }),
    [controller, state],
  );
}
